/**
 * apiClient — global fetch override. ALL API calls automatically encrypted.
 * Import once in App.jsx. No component changes needed.
 */
const API_HOST = 'https://api.krh1996.com';
const SECRET = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_SECRET) || 'krh1996-shared-secret';

// The proxy forwards no response headers and the shim cannot see a reason phrase, but the call sites
// that compose messages out of it ("HTTP ${status}: ${statusText}") only became reachable once `ok`
// could be false, so without this they would render "HTTP 404: undefined".
const STATUS_TEXT = {
  400: 'Bad Request', 401: 'Unauthorized', 403: 'Forbidden', 404: 'Not Found', 409: 'Conflict',
  410: 'Gone', 422: 'Unprocessable Entity', 429: 'Too Many Requests', 500: 'Internal Server Error',
  502: 'Bad Gateway', 503: 'Service Unavailable', 504: 'Gateway Timeout',
};

// ── XOR cipher ─────────────────────────────────────────────────────────────
function xorCipher(data, key) {
  let result = '';
  for (let i = 0; i < data.length; i++) {
    result += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return result;
}

function toB64(str) {
  return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64(str) {
  return decodeURIComponent(escape(atob(str.replace(/-/g, '+').replace(/_/g, '/'))));
}

// ── Global fetch override ──────────────────────────────────────────────────

const _origFetch = window.fetch.bind(window);

window.fetch = function (url, options = {}) {
  const urlStr = typeof url === 'string' ? url : url.toString();

  // Only intercept API calls
  if (!urlStr.includes(API_HOST)) return _origFetch(url, options);

  // Extract path + query from full URL
  const path = urlStr.replace(API_HOST, '');
  const method = (options.method || 'GET').toUpperCase();

  // Encrypt payload — include headers so proxy preserves Content-Type
  const payload = { p: path, m: method };
  if (options.headers) payload.h = options.headers;
  if (options.body) {
    try { payload.b = JSON.parse(options.body); } catch { payload.b = options.body; }
  }
  const encrypted = toB64(xorCipher(JSON.stringify(payload), SECRET));

  // Route through proxy
  return _origFetch(`${API_HOST}/api/c`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ d: encrypted }),
    credentials: 'include',
  }).then(async (res) => {
    const raw = await res.json();
    // The proxy leaves its own status at 200 for every non-paper request and sends the API's real
    // status beside the body as `s`. Falling back to the outer status when `s` is absent is what lets
    // this file and the proxy deploy in either order: each is inert without the other.
    const status = typeof raw.s === 'number' ? raw.s : res.status;
    // A body that is not JSON — a served file, an Express error page — has to survive as text.
    // Parsing it and falling back to the envelope on failure would hand the caller `{ d: <ciphertext> }`
    // instead of the content it asked for, which is what `res.text()` used to return.
    let data = raw;
    if (raw.d !== undefined) {
      const decoded = xorCipher(fromB64(raw.d), SECRET);
      try { data = JSON.parse(decoded); } catch { data = decoded; }
    }
    // Return Response-compatible object
    return {
      // Standard Response semantics, which also makes a non-paper call agree with a paper one: the
      // proxy already propagates paper statuses, so a redirect was never `ok` there either.
      ok: status >= 200 && status < 300,
      status,
      statusText: STATUS_TEXT[status] || '',
      json: async () => data,
      text: async () => (typeof data === 'string' ? data : JSON.stringify(data)),
      _data: data,
    };
  });
};

// ── Direct API (for code that wants explicit control) ──────────────────────

export async function apiCall(method, path, body, query) {
  let fullPath = path;
  if (query) {
    const qs = Object.entries(query)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    if (qs) fullPath += '?' + qs;
  }
  const payload = { p: fullPath, m: method };
  if (body) payload.b = body;
  const encrypted = toB64(xorCipher(JSON.stringify(payload), SECRET));
  const res = await _origFetch(`${API_HOST}/api/c`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ d: encrypted }),
    credentials: 'include',
  });
  const raw = await res.json();
  if (raw.d !== undefined) {
    const decoded = xorCipher(fromB64(raw.d), SECRET);
    try { return JSON.parse(decoded); } catch { return decoded; }
  }
  return raw;
}

export const api = {
  get: async (path, query) => apiCall('GET', path, null, query),
  post: async (path, body, query) => apiCall('POST', path, body, query),
  put: async (path, body, query) => apiCall('PUT', path, body, query),
  del: async (path, query) => apiCall('DELETE', path, null, query),
};
